// Full-page report card canvas designer.
// Architecture mirrors the certificate designer exactly:
//   - Layers use { x, y, w, h } in canvas-px (not %)
//   - Scale = DISPLAY_W / CANVAS_W applied on render
//   - Global mousemove/mouseup drag (no react-draggable)
//   - assetUrls map: S3 key → presigned URL
//   - Assets stored in definition.assets[]

import { useEffect, useState, useCallback, useRef } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import {
  templatesApi, masterTemplatesApi, uploadRcAssetApi, rcAssetUrlApi, rcAssetUrlsApi,
  listRcAssetFolders, listRcAssets,
  type ReportCardTemplate, type ReportCardMasterTemplate, type RcAsset,
} from "../../../service/report-card"
import { getAllSessions } from "../../../service/sessions"
import { getAllClasses } from "../../../service/classes"
import { getAllCourseStructures } from "../../../service/course-structure"
import { getAllTerms } from "../../../service/term-management"
import { selectCurrentBranch, selectBranches } from "../../../core/data/redux/slice/branchesSlice"
import { all_routes as routes } from "../../router/all_routes"
import {
  type Layer, type LayerType, type CanvasDefinition,
  CANVAS_W, CANVAS_H,
} from "./canvas/types"
import { createDefaultLayers } from "./canvas/defaultLayers"
import { PLACEHOLDERS, DATA_FIELDS, buildDataLayer, buildReplicas, type Placeholder, type DataField } from "./canvas/placeholders"
import Canvas from "./canvas/Canvas"
import LayerPanel from "./canvas/LayerPanel"

const genId = (type: string) => `${type}_${Date.now()}_${Math.random().toString(36).slice(2, 5)}`

const LAYER_DEFAULTS: Partial<Record<LayerType, Partial<Layer>>> = {
  text:           { w: 240, h: 40,  content: "Text Label", color: "#0f172a", fontSize: 16 },
  "dynamic-text": { w: 240, h: 40,  field: "", color: "#0f172a", fontSize: 14 },
  image:          { w: 200, h: 200, fit: "contain" },
  shape:          { w: 200, h: 80,  shape: "rectangle", fill: "#e2e8f0", stroke: "#94a3b8", strokeWidth: 1 },
  line:           { w: 400, h: 10,  color: "#0f172a", thickness: 2 },
  header:         { w: CANVAS_W, h: 135 },
  student_info:   { w: 600, h: 110, showPhoto: true },
  subject_table:  { w: CANVAS_W - 32, h: 450 },
  grade_key:      { w: 380, h: 100 },
  signatures:     { w: CANVAS_W - 32, h: 80 },
  attendance:     { w: 320, h: 60 },
}

const ReportCardTemplateDesigner = () => {
  const { id, masterId } = useParams<{ id?: string; masterId?: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // isMasterMode: editing a master template (route /master/:masterId)
  const isMasterMode = !!masterId
  const isNew = isMasterMode ? false : (!id || id === "new")

  // For new class templates: scope + optional master to snapshot layers from
  const fromMaster = !isMasterMode && isNew ? searchParams.get("from_master") : null
  const scopeSession = !isMasterMode && isNew ? searchParams.get("session") : null
  const scopeTerm   = !isMasterMode && isNew ? searchParams.get("term")    : null
  const scopeClass  = !isMasterMode && isNew ? searchParams.get("class")   : null

  const currentBranch = useSelector(selectCurrentBranch)
  const branches = useSelector(selectBranches)

  // ── Form state ──
  const [form, setForm] = useState<Partial<ReportCardTemplate>>({
    name: "",
    masterTemplate: null,
    branch: null,
    session: null,
    term: null,
    classes: [],
    schoolName: "",
    schoolTagline: "",
    columns: { showObtained: true, showTotal: true, showPercent: true, showGrade: true, showRemarks: true },
    showPosition: true,
    showPromotion: true,
    showGradeKey: true,
    signatures: ["Class Teacher", "Principal", "Date"],
    headerColor: "#0f172a",
    accentColor: "#1a56db",
    isActive: true,
  })
  const [sigText, setSigText] = useState("Class Teacher, Principal, Date")
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!isNew || isMasterMode)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [background, setBackground] = useState("#ffffff")
  const [showGrid, setShowGrid] = useState(false)
  const [zoomPct, setZoomPct] = useState(100)
  const [fullscreen, setFullscreen] = useState(false)
  const [realSubjectCount, setRealSubjectCount] = useState<number | null>(null)

  // ── Undo / redo history ──
  const histPast = useRef<Layer[][]>([])
  const histFuture = useRef<Layer[][]>([])
  const histSkip = useRef(false)
  const histPrev = useRef<Layer[]>([])
  const [histTick, setHistTick] = useState(0)

  // ── Canvas state ──
  const [layers, setLayers] = useState<Layer[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Unified selection: plain click selects one; shift-click toggles multi-select.
  const selectLayer = useCallback((id: string | null, additive = false) => {
    if (id == null) { setSelectedId(null); setSelectedIds(new Set()); return }
    if (additive) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.has(id) ? next.delete(id) : next.add(id)
        return next
      })
      setSelectedId(id)
    } else {
      setSelectedId(id)
      setSelectedIds(new Set([id]))
    }
  }, [])

  // ── Asset state ── (same pattern as cert designer)
  const [assets, setAssets] = useState<string[]>([])
  const [assetUrls, setAssetUrls] = useState<Record<string, string>>({})
  const [uploading, setUploading] = useState(false)

  // ── Asset library rail (S3 folder browser) ──
  const [assetFolders, setAssetFolders] = useState<string[]>([])
  const [assetFolder, setAssetFolder] = useState<string>("")
  const [folderAssets, setFolderAssets] = useState<RcAsset[]>([])
  const [loadingAssets, setLoadingAssets] = useState(false)
  const assetFileRef = useRef<HTMLInputElement>(null)

  // Mirror assetUrls in a ref so ensureAssetUrl never has a stale closure
  const assetUrlsRef = useRef<Record<string, string>>({})
  useEffect(() => { assetUrlsRef.current = assetUrls }, [assetUrls])
  // In-flight guard: prevents duplicate concurrent fetches for the same key
  const fetchingRef = useRef<Set<string>>(new Set())

  // ── Filter data ──
  const [masters, setMasters] = useState<ReportCardMasterTemplate[]>([])
  const [sessions, setSessions] = useState<any[]>([])
  const [terms, setTerms] = useState<any[]>([])
  const [classes, setClasses] = useState<any[]>([])

  // Stable presigned-URL resolver — no stale closure, no duplicate requests
  const ensureAssetUrl = useCallback(async (key: string) => {
    if (!key || assetUrlsRef.current[key] || fetchingRef.current.has(key)) return
    fetchingRef.current.add(key)
    try {
      const { url } = await rcAssetUrlApi(key)
      setAssetUrls(m => ({ ...m, [key]: url }))
    } catch {}
    finally { fetchingRef.current.delete(key) }
  }, [])  // stable — no deps needed

  // Resolve URLs whenever layers change (catches images added after initial load)
  useEffect(() => {
    layers.forEach(l => {
      if (l.type === "image" && l.src) ensureAssetUrl(l.src)
      // rms_campus_header stores a banner image URL inside _rmsProps.bannerByCampus
      if (l.type === "rms_campus_header") {
        const byC = (l._rmsProps as any)?.bannerByCampus as Record<string, any> | undefined
        const entry = byC ? Object.values(byC)[0] : null
        if (entry?.url) {
          const key = "rc-assets/" + String(entry.url).replace(/^\/+/, "").replace(/^report-card-templates\//, "")
          ensureAssetUrl(key)
        }
      }
    })
  }, [layers, ensureAssetUrl])

  // Load filter options
  useEffect(() => {
    const load = async () => {
      try {
        const [m, s, t, cl] = await Promise.all([
          masterTemplatesApi.list(),
          getAllSessions(),
          getAllTerms(),
          getAllClasses(),
        ])
        setMasters(m?.data || [])
        setSessions(Array.isArray(s) ? s : (s as any)?.sessions || (s as any)?.data || [])
        setTerms(Array.isArray(t) ? t : (t as any)?.data || (t as any)?.terms || [])
        setClasses(Array.isArray(cl) ? cl : (cl as any)?.classes || (cl as any)?.data || [])
      } catch {}
    }
    load()
  }, [])

  // Load template (+ master template layers as inherited/read-only background)
  useEffect(() => {
    // ── Master template editing ──
    if (isMasterMode) {
      setLoading(true)
      masterTemplatesApi.get(masterId!)
        .then(r => {
          const d = r.data as any
          setForm({ name: d.name, schoolName: d.schoolName, schoolTagline: d.schoolTagline, headerColor: d.headerColor, accentColor: d.accentColor, columns: d.columns, showGradeKey: d.showGradeKey, showPosition: d.showPosition, showPromotion: d.showPromotion, isActive: d.isActive })
          setSigText((d.signatures || []).join(", "))
          const def = d.definition as CanvasDefinition | null
          setBackground(def?.canvas?.background || "#ffffff")
          const savedLayers: Layer[] = (def?.layers || []) as Layer[]
          const savedAssets: string[] = (def as any)?.assets || []
          setAssets(savedAssets)
          const keys = new Set<string>()
          savedLayers.filter(l => l.type === "image" && l.src).forEach(l => keys.add(l.src!))
          savedAssets.forEach(k => keys.add(k))
          if (keys.size) {
            keys.forEach(k => fetchingRef.current.add(k))
            rcAssetUrlsApi(Array.from(keys))
              .then(map => setAssetUrls(m => ({ ...m, ...map })))
              .catch(() => {})
              .finally(() => keys.forEach(k => fetchingRef.current.delete(k)))
          }
          setLayers(savedLayers.length ? savedLayers : createDefaultLayers(d.headerColor || "#0f172a"))
        })
        .catch(() => toast.error("Failed to load master template"))
        .finally(() => setLoading(false))
      return
    }

    // ── New class template (from scratch or from master) ──
    if (isNew) {
      const preForm: Partial<ReportCardTemplate> = {
        name: "",
        masterTemplate: null,
        branch: null,
        session: scopeSession as any || null,
        term: scopeTerm as any || null,
        classes: scopeClass ? [scopeClass as any] : [],
        schoolName: "",
        schoolTagline: "",
        columns: { showObtained: true, showTotal: true, showPercent: true, showGrade: true, showRemarks: true },
        showPosition: true, showPromotion: true, showGradeKey: true,
        signatures: ["Class Teacher", "Principal", "Date"],
        headerColor: "#0f172a", accentColor: "#1a56db", isActive: true,
      }
      if (!fromMaster) {
        setForm(preForm)
        setLayers(createDefaultLayers(preForm.headerColor || "#0f172a"))
        return
      }
      // Snapshot master layers as inherited background
      setLoading(true)
      masterTemplatesApi.get(fromMaster)
        .then(r => {
          const masterDoc = r.data as any
          const masterDef = masterDoc.definition as CanvasDefinition | null
          const inheritedLayers = (masterDef?.layers || []) as Layer[]
          const inheritedWithFlag = inheritedLayers.map(l => ({ ...l, _inherited: true, locked: true }))
          setForm({ ...preForm, masterTemplate: fromMaster as any, headerColor: masterDoc.headerColor || "#0f172a", accentColor: masterDoc.accentColor || "#1a56db" })
          const keys = new Set<string>()
          inheritedWithFlag.filter(l => l.type === "image" && l.src).forEach(l => keys.add(l.src!))
          if (keys.size) {
            keys.forEach(k => fetchingRef.current.add(k))
            rcAssetUrlsApi(Array.from(keys))
              .then(map => setAssetUrls(m => ({ ...m, ...map })))
              .catch(() => {})
              .finally(() => keys.forEach(k => fetchingRef.current.delete(k)))
          }
          setLayers(inheritedWithFlag.length ? inheritedWithFlag : createDefaultLayers(masterDoc.headerColor || "#0f172a"))
        })
        .catch(() => {
          setForm(preForm)
          setLayers(createDefaultLayers(preForm.headerColor || "#0f172a"))
        })
        .finally(() => setLoading(false))
      return
    }

    // ── Existing class template ──
    setLoading(true)
    templatesApi.get(id!)
      .then(async r => {
        const d = r.data
        setForm(d)
        setSigText((d.signatures || []).join(", "))
        const def = d.definition as CanvasDefinition | null
        setBackground(def?.canvas?.background || "#ffffff")
        const savedLayers: Layer[] = (def?.layers || []) as Layer[]
        const savedAssets: string[] = (def as any)?.assets || []
        setAssets(savedAssets)

        // Load master template layers (rendered underneath, read-only)
        let masterLayers: Layer[] = []
        const masterRef = typeof d.masterTemplate === "object"
          ? (d.masterTemplate as any)?._id
          : d.masterTemplate
        if (masterRef) {
          try {
            const mr = await masterTemplatesApi.get(masterRef)
            const masterDef = (mr.data as any)?.definition as CanvasDefinition | null
            if (masterDef?.layers?.length) {
              masterLayers = (masterDef.layers as Layer[]).map(l => ({
                ...l,
                _inherited: true,
                locked: true,
              }))
            }
          } catch {}
        }

        const keys = new Set<string>()
        savedLayers.filter(l => l.type === "image" && l.src).forEach(l => keys.add(l.src!))
        masterLayers.filter(l => l.type === "image" && l.src).forEach(l => keys.add(l.src!))
        savedAssets.forEach(key => keys.add(key))
        if (keys.size) {
          keys.forEach(k => fetchingRef.current.add(k))
          rcAssetUrlsApi(Array.from(keys))
            .then(map => setAssetUrls(m => ({ ...m, ...map })))
            .catch(() => {})
            .finally(() => keys.forEach(k => fetchingRef.current.delete(k)))
        }

        const allLayers = [...masterLayers, ...savedLayers]
        setLayers(allLayers.length ? allLayers : createDefaultLayers(d.headerColor || "#0f172a"))
      })
      .catch(() => toast.error("Failed to load template"))
      .finally(() => setLoading(false))
  }, [id, masterId])

  // Asset rail: fetch the folder list once.
  useEffect(() => { listRcAssetFolders().then(setAssetFolders).catch(() => {}) }, [])

  // Default the asset folder to the one this template's images live in.
  useEffect(() => {
    if (assetFolder) return
    const firstImg = layers.find(l => l.type === "image" && (l as any).src)?.src as string | undefined
    const m = firstImg?.match(/^rc-assets\/([^/]+)\//)
    if (m) setAssetFolder(m[1])
  }, [layers, assetFolder])

  // Load images for the selected folder + merge their presigned URLs.
  useEffect(() => {
    if (!assetFolder) return
    setLoadingAssets(true)
    listRcAssets(assetFolder)
      .then(items => {
        setFolderAssets(items)
        setAssetUrls(m => { const next = { ...m }; for (const it of items) next[it.key] = it.url; return next })
      })
      .catch(() => setFolderAssets([]))
      .finally(() => setLoadingAssets(false))
  }, [assetFolder])

  // Real-subjects badge — count course structures for this template's scope.
  useEffect(() => {
    if (isMasterMode) { setRealSubjectCount(null); return }
    const sid = (form.session as any)?._id || form.session
    const cid = (Array.isArray(form.classes) ? (form.classes[0] as any) : null)?._id || (Array.isArray(form.classes) ? form.classes[0] : null)
    const bid = (form.branch as any)?._id || form.branch || currentBranch?._id
    if (!sid || !cid) { setRealSubjectCount(null); return }
    let cancelled = false
    getAllCourseStructures({ session: sid as string, class: cid as string, branch: bid as string, limit: 1 })
      .then(r => { if (!cancelled) setRealSubjectCount(r?.pagination?.total ?? (r?.data?.length || 0)) })
      .catch(() => { if (!cancelled) setRealSubjectCount(null) })
    return () => { cancelled = true }
  }, [form.session, form.classes, form.branch, isMasterMode])

  const set = (patch: Partial<ReportCardTemplate>) => setForm(f => ({ ...f, ...patch }))

  const buildDefinition = (): CanvasDefinition => ({
    canvas: { width: CANVAS_W, height: CANVAS_H, background },
    assets,
    // Exclude inherited master layers — they live in the master template, not here
    layers: layers.filter(l => !l._inherited),
  })

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error("Template name is required"); return }
    setSaving(true)
    try {
      const sigs = sigText.split(",").map(s => s.trim()).filter(Boolean)

      if (isMasterMode) {
        // Saving a master template — no scope fields
        const payload = {
          name: form.name,
          schoolName: form.schoolName,
          schoolTagline: form.schoolTagline,
          headerColor: form.headerColor,
          accentColor: form.accentColor,
          columns: form.columns,
          showGradeKey: form.showGradeKey,
          showPosition: form.showPosition,
          showPromotion: form.showPromotion,
          signatures: sigs,
          definition: buildDefinition(),
        }
        await masterTemplatesApi.update(masterId!, payload)
        toast.success("Master template saved")
        navigate(routes.reportCardDesigner)
        return
      }

      const payload = {
        ...form,
        signatures: sigs,
        masterTemplate: (form.masterTemplate as any)?._id || form.masterTemplate || null,
        session: (form.session as any)?._id || form.session || null,
        term: (form.term as any)?._id || form.term || null,
        classes: ((form.classes || []) as any[]).map(c => c._id || c),
        branch: (form.branch as any)?._id || form.branch || currentBranch?._id || null,
        definition: buildDefinition(),
      }
      if (isNew) {
        await templatesApi.create(payload)
        toast.success("Template created")
      } else {
        await templatesApi.update(id!, payload)
        toast.success("Template saved")
      }
      navigate(routes.reportCardDesigner)
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  // ── Layer operations ──
  const handleLayerMove = useCallback((layerId: string, x: number, y: number) => {
    setLayers(ls => ls.map(l => l.id === layerId ? { ...l, x, y } : l))
  }, [])

  const handleLayerChange = useCallback((layerId: string, patch: Partial<Layer>) => {
    setLayers(ls => ls.map(l => l.id === layerId ? { ...l, ...patch } : l))
  }, [])

  const handleLayersChange = useCallback((next: Layer[]) => setLayers(next), [])

  const handleDelete = useCallback((layerId: string) => {
    setLayers(ls => ls.filter(l => l.id !== layerId))
    setSelectedId(id => id === layerId ? null : id)
  }, [])

  const handleMoveUp = useCallback((layerId: string) => {
    setLayers(ls => {
      const idx = ls.findIndex(l => l.id === layerId)
      if (idx < 0 || idx >= ls.length - 1) return ls
      const next = [...ls]
      ;[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]]
      return next
    })
  }, [])

  const handleMoveDown = useCallback((layerId: string) => {
    setLayers(ls => {
      const idx = ls.findIndex(l => l.id === layerId)
      if (idx <= 0) return ls
      const next = [...ls]
      ;[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]]
      return next
    })
  }, [])

  const handleAddLayer = (type: LayerType, extraProps?: Partial<Layer>) => {
    const defaults = LAYER_DEFAULTS[type] || {}
    const newLayer: Layer = {
      id: genId(type),
      type,
      x: 32, y: 32,
      w: defaults.w || 200,
      h: defaults.h || 80,
      rotation: 0,
      opacity: 1,
      locked: false,
      headerColor: form.headerColor || "#0f172a",
      accentColor: form.accentColor || "#1a56db",
      ...defaults,
      ...extraProps,
    }
    setLayers(ls => [...ls, newLayer])
    setSelectedId(newLayer.id)
  }

  // ── Asset upload (left panel) ──
  const handleAssetUpload = async (file: File) => {
    setUploading(true)
    try {
      const { key, url } = await uploadRcAssetApi(file)
      setAssets(a => [...a, key])
      setAssetUrls(m => ({ ...m, [key]: url }))
    } catch {
      toast.error("Asset upload failed")
    } finally {
      setUploading(false)
    }
  }

  // Drop an RMS placeholder (1+ layers) and select the first.
  const addPlaceholder = (p: Placeholder) => {
    const folderCount = layers.filter(l => l.type === "folder").length
    const built = p.build(genId, folderCount)
    setLayers(ls => [...ls, ...built])
    setSelectedId(built[0]?.id || null)
  }
  // Drop a data-field token as a text layer.
  const addDataField = (f: DataField) => {
    const l = buildDataLayer(genId, f)
    setLayers(ls => [...ls, l])
    setSelectedId(l.id)
  }
  // Replicate a styled prototype subject card across all subjects.
  const handleReplicate = (proto: Layer) => {
    const folderCount = layers.filter(l => l.type === "folder").length
    const replicas = buildReplicas(proto, genId, folderCount)
    setLayers(ls => [...ls, ...replicas])
    toast.success(`Replicated to ${replicas.length / 2} subjects`)
  }

  // Track layer changes into undo history (skips the change caused by undo/redo).
  useEffect(() => {
    if (histSkip.current) { histSkip.current = false; histPrev.current = layers; return }
    if (histPrev.current.length && histPrev.current !== layers) {
      histPast.current.push(histPrev.current)
      if (histPast.current.length > 60) histPast.current.shift()
      histFuture.current = []
      setHistTick(t => t + 1)
    }
    histPrev.current = layers
  }, [layers])

  const undo = () => {
    const prev = histPast.current.pop()
    if (!prev) return
    histFuture.current.push(layers)
    histSkip.current = true
    setLayers(prev)
    setHistTick(t => t + 1)
  }
  const redo = () => {
    const next = histFuture.current.pop()
    if (!next) return
    histPast.current.push(layers)
    histSkip.current = true
    setLayers(next)
    setHistTick(t => t + 1)
  }
  const clearLayers = () => {
    if (window.confirm("Clear all your layers? (master layers are kept)")) {
      setLayers(ls => ls.filter(l => l._inherited))
    }
  }
  void histTick

  // Wrap the selected layers into a folder; first-selected becomes the anchor.
  const groupSelection = () => {
    const ids = Array.from(selectedIds).filter(id => {
      const l = layers.find(x => x.id === id)
      return l && l.type !== "folder" && !l._inherited
    })
    if (ids.length < 2) { toast.info("Shift-click 2+ layers on the canvas, then Group."); return }
    const gid = genId("folder")
    const folderCount = layers.filter(l => l.type === "folder").length
    const folder = { id: gid, type: "folder", name: `Group ${folderCount + 1}`, hidden: false } as any
    setLayers(ls => {
      const updated = ls.map(l => ids.includes(l.id) ? { ...l, groupId: gid, isFolderAnchor: l.id === ids[0] } as Layer : l)
      // Insert the folder row just before its first member.
      const firstIdx = updated.findIndex(l => l.id === ids[0])
      return [...updated.slice(0, firstIdx), folder, ...updated.slice(firstIdx)]
    })
    toast.success(`Grouped ${ids.length} layers`)
  }
  // Dissolve the folder of the current selection.
  const dissolveSelection = () => {
    const sel = layers.find(l => l.id === selectedId) as any
    const gid = sel?.type === "folder" ? sel.id : sel?.groupId
    if (!gid) { toast.info("Select a folder or a layer inside one."); return }
    setLayers(ls => ls
      .filter(l => !(l.type === "folder" && l.id === gid))
      .map(l => (l as any).groupId === gid ? { ...l, groupId: null, isFolderAnchor: false } as Layer : l))
  }
  const canGroup = selectedIds.size >= 2
  const canDissolve = (() => {
    const sel = layers.find(l => l.id === selectedId) as any
    return sel?.type === "folder" || !!sel?.groupId
  })()

  // ── Canvas tools (shift design + snap) ──
  const [nudgeStep, setNudgeStep] = useState(1)
  const shiftAll = (dx: number, dy: number) => {
    setLayers(ls => ls.map(l => l._inherited || l.type === "folder" ? l : {
      ...l,
      leftPct: ((l as any).leftPct ?? (l.x / CANVAS_W) * 100) + dx,
      topPct: ((l as any).topPct ?? (l.y / CANVAS_H) * 100) + dy,
      x: l.x + Math.round((dx / 100) * CANVAS_W),
      y: l.y + Math.round((dy / 100) * CANVAS_H),
    } as Layer))
  }
  // Snap the selected layer to a canvas edge / centre.
  const snapSelected = (where: "left" | "hcenter" | "right" | "top" | "vcenter" | "bottom") => {
    if (!selectedId) return
    const l = layers.find(x => x.id === selectedId) as any
    if (!l) return
    const w = l.widthPct ?? (l.w / CANVAS_W) * 100
    const h = l.heightPct ?? (l.h / CANVAS_H) * 100
    const patch: any = {}
    if (where === "left") patch.leftPct = 0
    if (where === "hcenter") patch.leftPct = (100 - w) / 2
    if (where === "right") patch.leftPct = 100 - w
    if (where === "top") patch.topPct = 0
    if (where === "vcenter") patch.topPct = (100 - h) / 2
    if (where === "bottom") patch.topPct = 100 - h
    if (patch.leftPct != null) patch.x = Math.round((patch.leftPct / 100) * CANVAS_W)
    if (patch.topPct != null) patch.y = Math.round((patch.topPct / 100) * CANVAS_H)
    handleLayerChange(selectedId, patch)
  }

  // Click asset thumbnail → add as image layer
  const handleAssetClick = (key: string) => {
    const url = assetUrls[key]
    // Estimate size: default 200×200, user can resize
    handleAddLayer("image", { src: key, w: 200, h: 200 })
    if (url) setAssetUrls(m => ({ ...m, [key]: url }))  // already in map
  }

  const scopeBreadcrumb = [
    (form.session as any)?.title,
    currentBranch?.name,
    (form.term as any)?.name || (form.term as any)?.title,
    Array.isArray(form.classes) ? (form.classes as any[]).map(c => c?.name).filter(Boolean).join(", ") : null,
  ].filter(Boolean).join(" · ")

  const tplForCanvas = {
    schoolName: form.schoolName || currentBranch?.name,
    schoolTagline: form.schoolTagline,
    headerColor: form.headerColor,
    accentColor: form.accentColor,
    // Campus logo + banner image come from the scoped branch (populated on load).
    logoKey: (form.branch as any)?.logoKey || (currentBranch as any)?.logoKey || form.logoKey,
    headerImageKey: (form.branch as any)?.headerImageKey || (currentBranch as any)?.headerImageKey,
    columns: form.columns as Record<string, boolean>,
    signatures: sigText.split(",").map(s => s.trim()).filter(Boolean),
  }

  if (loading) return (
    <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", zIndex: 1050 }}>
      <div className="spinner-border text-primary" />
    </div>
  )

  // Only look up the linked master for class templates (not when editing a master itself)
  const masterDoc = isMasterMode ? null : masters.find(m => m._id === ((form.masterTemplate as any)?._id || form.masterTemplate))

  return (
    <div className={fullscreen ? undefined : "page-wrapper"}>
    <div className="rcd-root" style={fullscreen
      ? { position: "fixed", inset: 0, display: "flex", flexDirection: "column", background: "#fff", zIndex: 1050 }
      : { display: "flex", flexDirection: "column", background: "#fff", height: "calc(100vh - 56px)" }}>

      {/* Responsive rules for the designer across all screen sizes. */}
      <style>{`
        .rcd-toolbar { flex-wrap: wrap; row-gap: 6px; }
        .rcd-main { min-height: 0; }
        @media (max-width: 1200px) {
          .rcd-leftrail { width: 150px !important; }
          .rcd-rightpanel { width: 270px !important; }
        }
        @media (max-width: 992px) {
          .rcd-main { overflow-x: auto !important; }
          .rcd-leftrail { width: 140px !important; }
          .rcd-rightpanel { width: 250px !important; }
        }
        @media (max-width: 680px) {
          .rcd-leftrail { width: 120px !important; }
          .rcd-rightpanel { width: 230px !important; }
          .rcd-toolbar { padding: 6px 10px !important; }
        }
      `}</style>

      {/* ── Top toolbar ── */}
      <div className="rcd-toolbar" style={{ minHeight: 52, borderBottom: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", padding: "0 16px", gap: 10, flexShrink: 0, zIndex: 10 }}>
        <span style={{ cursor: "pointer", fontSize: 13, color: "#6b7280" }} onClick={() => navigate(routes.reportCardDesigner)}>
          <i className="ti ti-arrow-left me-1" />Report Cards
        </span>
        <span style={{ color: "#d1d5db" }}>/</span>
        {isMasterMode && <span style={{ fontSize: 11, background: "#eef2ff", color: "#2e3192", borderRadius: 4, padding: "2px 6px", fontWeight: 600 }}>MASTER</span>}
        <input
          value={form.name || ""}
          onChange={e => set({ name: e.target.value })}
          placeholder={isMasterMode ? "Master template name…" : "Template name…"}
          style={{ border: "none", outline: "none", fontSize: 14, fontWeight: 600, color: "#111827", background: "transparent", minWidth: 160 }}
        />

        {/* Scope breadcrumb + real-subjects badge */}
        {!isMasterMode && scopeBreadcrumb && (
          <span style={{ fontSize: 10, fontFamily: "monospace", color: "#64748b", background: "#f1f5f9", borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 280 }} title={scopeBreadcrumb}>
            {scopeBreadcrumb}
          </span>
        )}
        {!isMasterMode && realSubjectCount != null && (
          realSubjectCount > 0 ? (
            <span style={{ fontSize: 10, fontWeight: 600, color: "#047857", background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap" }} title="Real course-structure data for this scope">📚 {realSubjectCount} real subjects</span>
          ) : (
            <span style={{ fontSize: 10, fontWeight: 600, color: "#2e3192", background: "#eef2ff", border: "1px solid #dfe6ff", borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap" }} title="No course structures for this scope — preview uses sample data">⚠ sample data</span>
          )
        )}

        <div style={{ flex: 1 }} />

        {/* Folder / Dissolve */}
        <div className="d-flex align-items-center" style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0, fontSize: 11 }} title="Shift-click 2+ layers, then group them into a folder" disabled={!canGroup} onClick={groupSelection}><i className="ti ti-folder-plus" /></button>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0, fontSize: 11, borderLeft: "1px solid #e2e8f0" }} title="Dissolve the selected folder" disabled={!canDissolve} onClick={dissolveSelection}><i className="ti ti-folder-x" /></button>
        </div>

        {/* Undo / Redo */}
        <div className="d-flex align-items-center" style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0, fontWeight: 700 }} title="Undo (Ctrl+Z)" disabled={histPast.current.length === 0} onClick={undo}>↶</button>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0, fontWeight: 700, borderLeft: "1px solid #e2e8f0" }} title="Redo (Ctrl+Shift+Z)" disabled={histFuture.current.length === 0} onClick={redo}>↷</button>
        </div>

        {/* 5% calibration grid */}
        <label className="d-flex align-items-center gap-1" style={{ fontSize: 11, color: "#6b7280", cursor: "pointer", margin: 0 }} title="5% calibration grid">
          <input type="checkbox" checked={showGrid} onChange={e => setShowGrid(e.target.checked)} />
          Grid
        </label>

        {/* Zoom */}
        <div className="d-flex align-items-center" style={{ border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0 }} title="Zoom out" disabled={zoomPct <= 25} onClick={() => setZoomPct(z => Math.max(25, z - 25))}>−</button>
          <span style={{ fontSize: 11, fontFamily: "monospace", width: 40, textAlign: "center" }}>{zoomPct}%</span>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0 }} title="Zoom in" disabled={zoomPct >= 200} onClick={() => setZoomPct(z => Math.min(200, z + 25))}>+</button>
          <button className="btn btn-sm btn-light" style={{ padding: "2px 8px", borderRadius: 0, borderLeft: "1px solid #e2e8f0", fontSize: 10 }} title="Reset to 100%" onClick={() => setZoomPct(100)}>Fit</button>
        </div>

        {/* Background color */}
        <div className="d-flex align-items-center gap-1">
          <label style={{ fontSize: 11, color: "#6b7280" }}>BG</label>
          <input type="color" className="form-control form-control-color" style={{ width: 32, height: 28 }}
            value={background} onChange={e => setBackground(e.target.value)} />
        </div>

        <button className="btn btn-sm btn-light" onClick={() => window.print()} title="Test print this canvas">
          <i className="ti ti-printer" />
        </button>
        <button className="btn btn-sm btn-light text-danger" onClick={clearLayers} title="Clear your layers">
          <i className="ti ti-trash" />
        </button>
        <button className="btn btn-sm btn-light" onClick={() => setFullscreen(f => !f)} title={fullscreen ? "Exit full screen" : "Full screen"}>
          <i className={`ti ${fullscreen ? "ti-arrows-minimize" : "ti-arrows-maximize"}`} />
        </button>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setSettingsOpen(o => !o)}>
          <i className="ti ti-settings me-1" />Settings
        </button>
        <button className="btn btn-sm btn-light" onClick={() => navigate(routes.reportCardDesigner)}>Cancel</button>
        <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
          <i className="ti ti-device-floppy me-1" />
          {saving ? "Saving…" : isNew ? "Create" : "Update"}
        </button>
      </div>

      {/* Banner: master-editing notice OR class-template master link */}
      {isMasterMode ? (
        <div style={{ background: "#eef2ff", borderBottom: "1px solid #dfe6ff", padding: "5px 16px", fontSize: 11, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <i className="ti ti-crown text-warning" />
          <strong>Master template</strong>
          <span className="text-muted"> — These layers will be inherited by all class templates linked to this master.</span>
        </div>
      ) : masterDoc && (
        <div style={{ background: "#eef2ff", borderBottom: "1px solid #dfe6ff", padding: "5px 16px", fontSize: 11, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <i className="ti ti-link text-warning" />
          <strong>Master: {masterDoc.name}</strong>
          <span className="text-muted"> — Master layers update automatically when master is saved.</span>
        </div>
      )}

      <div className="rcd-main" style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* ── LEFT: Settings panel (collapsible) ── */}
        {settingsOpen && (
          <div className="rcd-settings" style={{ width: 270, borderRight: "1px solid #e2e8f0", overflowY: "auto", background: "#fff", flexShrink: 0, padding: 12, fontSize: 12 }}>
            <h6 className="fw-bold mb-3" style={{ fontSize: 13 }}>Template Settings</h6>

            {/* ── Canvas tools ── */}
            <div className="text-muted fw-semibold mb-1" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Canvas</div>
            <div className="mb-2" style={{ fontSize: 11, color: "#64748b" }}>Size: A4 portrait · 794 × 1123 px</div>
            <div className="mb-1" style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Shift design (nudge all layers)</div>
            <div className="d-flex align-items-center gap-1 mb-2">
              <button className="btn btn-sm btn-light" onClick={() => shiftAll(0, -nudgeStep)} title="Up">↑</button>
              <button className="btn btn-sm btn-light" onClick={() => shiftAll(-nudgeStep, 0)} title="Left">←</button>
              <button className="btn btn-sm btn-light" onClick={() => shiftAll(nudgeStep, 0)} title="Right">→</button>
              <button className="btn btn-sm btn-light" onClick={() => shiftAll(0, nudgeStep)} title="Down">↓</button>
              <input type="number" className="form-control form-control-sm" style={{ width: 56, fontSize: 11 }} value={nudgeStep} onChange={e => setNudgeStep(Number(e.target.value) || 1)} title="Step %" />
              <span style={{ fontSize: 10, color: "#94a3b8" }}>%</span>
            </div>
            <div className="mb-1" style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Snap selected layer</div>
            <div className="d-flex flex-wrap gap-1 mb-3">
              {([["⤒","top"],["⟱","bottom"],["⤓","vcenter"],["⇤","left"],["⇥","right"],["⇔","hcenter"]] as const).map(([icon, w]) => (
                <button key={w} className="btn btn-sm btn-light" style={{ fontSize: 11 }} disabled={!selectedId} title={`Snap ${w}`} onClick={() => snapSelected(w)}>{icon}</button>
              ))}
            </div>

            <div className="mb-2">
              <label className="form-label form-label-sm">{isMasterMode ? "Master Name *" : "Template Name *"}</label>
              <input type="text" className="form-control form-control-sm" value={form.name || ""} onChange={e => set({ name: e.target.value })} />
            </div>

            {/* Class templates only: master template link + scope */}
            {!isMasterMode && (
              <>
                <div className="mb-3">
                  <label className="form-label form-label-sm">Master Template</label>
                  <select className="form-select form-select-sm" value={((form.masterTemplate as any)?._id || form.masterTemplate || "") as string} onChange={e => set({ masterTemplate: (e.target.value || null) as any })}>
                    <option value="">None</option>
                    {masters.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                  </select>
                </div>

                <div className="text-muted fw-semibold mb-2" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Scope</div>
                {branches.length > 1 && (
                  <div className="mb-2">
                    <label className="form-label form-label-sm">Campus</label>
                    <select className="form-select form-select-sm" value={((form.branch as any)?._id || form.branch || "") as string} onChange={e => set({ branch: (e.target.value || null) as any })}>
                      <option value="">Shared</option>
                      {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="mb-2">
                  <label className="form-label form-label-sm">Session</label>
                  <select className="form-select form-select-sm" value={((form.session as any)?._id || form.session || "") as string} onChange={e => set({ session: (e.target.value || null) as any })}>
                    <option value="">Any</option>
                    {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label form-label-sm">Term</label>
                  <select className="form-select form-select-sm" value={((form.term as any)?._id || form.term || "") as string} onChange={e => set({ term: (e.target.value || null) as any })}>
                    <option value="">Any</option>
                    {terms.map(t => <option key={t._id} value={t._id}>{(t as any).title || (t as any).name}</option>)}
                  </select>
                </div>
              </>
            )}

            <div className="text-muted fw-semibold mb-2" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Header</div>
            <div className="mb-2">
              <label className="form-label form-label-sm">School Name</label>
              <input type="text" className="form-control form-control-sm" placeholder="Defaults to branch name" value={form.schoolName || ""} onChange={e => set({ schoolName: e.target.value })} />
            </div>
            <div className="mb-3">
              <label className="form-label form-label-sm">Tagline</label>
              <input type="text" className="form-control form-control-sm" value={form.schoolTagline || ""} onChange={e => set({ schoolTagline: e.target.value })} />
            </div>

            <div className="text-muted fw-semibold mb-2" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Colors</div>
            <div className="d-flex gap-3 mb-3">
              <div>
                <label className="form-label form-label-sm">Header</label>
                <input type="color" className="form-control form-control-color" style={{ width: 40, height: 30 }} value={form.headerColor || "#0f172a"} onChange={e => set({ headerColor: e.target.value })} />
              </div>
              <div>
                <label className="form-label form-label-sm">Accent</label>
                <input type="color" className="form-control form-control-color" style={{ width: 40, height: 30 }} value={form.accentColor || "#1a56db"} onChange={e => set({ accentColor: e.target.value })} />
              </div>
              <div>
                <label className="form-label form-label-sm">BG</label>
                <input type="color" className="form-control form-control-color" style={{ width: 40, height: 30 }} value={background} onChange={e => setBackground(e.target.value)} />
              </div>
            </div>

            <div className="text-muted fw-semibold mb-2" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Subject Columns</div>
            {[
              { key: "showObtained", label: "Obtained" },
              { key: "showTotal", label: "Total" },
              { key: "showPercent", label: "%" },
              { key: "showGrade", label: "Grade" },
              { key: "showRemarks", label: "Result" },
            ].map(c => (
              <div key={c.key} className="form-check form-switch mb-1">
                <input type="checkbox" className="form-check-input" role="switch" id={c.key}
                  checked={(form.columns?.[c.key as keyof typeof form.columns] as boolean) ?? true}
                  onChange={e => set({ columns: { ...form.columns, [c.key]: e.target.checked } })} />
                <label className="form-check-label" htmlFor={c.key} style={{ fontSize: 11 }}>{c.label}</label>
              </div>
            ))}

            <div className="text-muted fw-semibold mb-2 mt-3" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Signatures</div>
            <input type="text" className="form-control form-control-sm mb-1" placeholder="Class Teacher, Principal, Date"
              value={sigText} onChange={e => setSigText(e.target.value)} />
            <div className="d-flex flex-wrap gap-1 mb-2">
              {sigText.split(",").filter(Boolean).map((s, i) => <span key={i} className="badge bg-secondary-transparent">{s.trim()}</span>)}
            </div>

            <div className="text-muted fw-semibold mb-2 mt-3" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>Sections</div>
            {[
              { key: "showPosition", label: "Class Position" },
              { key: "showPromotion", label: "Promotion Status" },
              { key: "showGradeKey", label: "Grade Key" },
            ].map(s => (
              <div key={s.key} className="form-check form-switch mb-1">
                <input type="checkbox" className="form-check-input" role="switch" id={s.key}
                  checked={(form[s.key as keyof typeof form] as boolean) ?? true}
                  onChange={e => set({ [s.key]: e.target.checked } as Partial<ReportCardTemplate>)} />
                <label className="form-check-label" htmlFor={s.key} style={{ fontSize: 11 }}>{s.label}</label>
              </div>
            ))}

            <div className="form-check form-switch mt-3">
              <input type="checkbox" className="form-check-input" role="switch" id="isActive"
                checked={form.isActive ?? true} onChange={e => set({ isActive: e.target.checked })} />
              <label className="form-check-label fw-semibold" htmlFor="isActive" style={{ fontSize: 11 }}>Active</label>
            </div>
          </div>
        )}

        {/* ── LEFT: Assets + Placeholders panel ── */}
        <div className="rcd-leftrail" style={{ width: 168, borderRight: "1px solid #e2e8f0", overflowY: "auto", background: "#fafafa", flexShrink: 0, padding: "10px 8px" }}>

          {/* Template folder — selects which asset folder the rail shows (RMS top control) */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 0.5, marginBottom: 3 }}>TEMPLATE FOLDER</div>
          <select
            className="form-select form-select-sm"
            style={{ fontSize: 11, padding: "2px 6px", marginBottom: 10 }}
            value={assetFolder}
            onChange={e => setAssetFolder(e.target.value)}
          >
            {assetFolders.length === 0 && <option value="">— loading —</option>}
            {assetFolders.map(f => <option key={f} value={f}>{f}</option>)}
          </select>

          {/* ASSETS section */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 0.5 }}>
              ASSETS ({folderAssets.length})
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: 9, padding: "1px 5px", lineHeight: 1.4 }}
              disabled={uploading}
              onClick={() => assetFileRef.current?.click()}
              title="Upload image asset"
            >
              {uploading ? <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10 }} /> : <i className="ti ti-upload" />}
            </button>
          </div>

          <input
            ref={assetFileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={async e => {
              const file = e.target.files?.[0]
              if (file) await handleAssetUpload(file)
              e.target.value = ""
            }}
          />

          {loadingAssets ? (
            <div style={{ fontSize: 9, color: "#9ca3af", padding: "8px 0 12px", textAlign: "center" }}>
              <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> Loading…
            </div>
          ) : folderAssets.length === 0 ? (
            <div style={{ fontSize: 9, color: "#9ca3af", padding: "8px 0 12px" }}>
              No images in this folder. Upload one above.
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 12 }}>
              {folderAssets.map(a => (
                <div
                  key={a.key}
                  title={`${a.name} — click to add as image layer`}
                  onClick={() => { setAssetUrls(m => ({ ...m, [a.key]: a.url })); handleAssetClick(a.key) }}
                  style={{ cursor: "pointer", border: "1px solid #e2e8f0", borderRadius: 4, overflow: "hidden", aspectRatio: "1", background: "#e5e7eb", position: "relative" }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = "#6366f1")}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = "#e2e8f0")}
                >
                  <img src={a.url} alt={a.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                </div>
              ))}
            </div>
          )}

          {/* PLACEHOLDERS section — RMS-typed layer droppers */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 0.5, marginBottom: 6 }}>PLACEHOLDERS</div>
          {PLACEHOLDERS.filter(p => !(p.key.startsWith("subject_group") && isMasterMode)).map(p => (
            <div
              key={p.key}
              onClick={() => addPlaceholder(p)}
              style={{ cursor: "pointer", background: p.styled ? "#f0fdfa" : "#fff", border: `1px solid ${p.styled ? "#99f6e4" : "#e2e8f0"}`, borderRadius: 5, padding: "6px 7px", marginBottom: 5 }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "#6366f1")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = p.styled ? "#99f6e4" : "#e2e8f0")}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 1 }}>
                <i className={`ti ${p.icon}`} style={{ fontSize: 12, color: p.styled ? "#0d9488" : "#6366f1" }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: p.styled ? "#0f766e" : "#374151" }}>{p.label}</span>
              </div>
              <div style={{ fontSize: 9, color: "#9ca3af", lineHeight: 1.3 }}>{p.desc}</div>
            </div>
          ))}

          {/* DATA FIELDS — token chips, each drops a text layer */}
          <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", letterSpacing: 0.5, marginTop: 10, marginBottom: 6 }}>
            DATA FIELDS ({DATA_FIELDS.length})
          </div>
          {DATA_FIELDS.map(f => (
            <div
              key={f.token}
              onClick={() => addDataField(f)}
              title={`Insert ${f.token} as a text layer`}
              style={{ cursor: "pointer", background: "#ecfdf5", border: "1px solid #d1fae5", borderRadius: 5, padding: "4px 7px", marginBottom: 4 }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "#34d399")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "#d1fae5")}
            >
              <div style={{ fontSize: 10.5, fontWeight: 600, color: "#374151" }}>{f.label}</div>
              <div style={{ fontSize: 9, color: "#10b981", fontFamily: "monospace" }}>{f.token}</div>
            </div>
          ))}
        </div>

        {/* ── CENTER: A4 Canvas ── */}
        <Canvas
          layers={layers}
          selectedId={selectedId}
          assetUrls={assetUrls}
          background={background}
          tpl={tplForCanvas}
          zoomPct={zoomPct}
          showGrid={showGrid}
          selectedIds={selectedIds}
          onSelect={selectLayer}
          onLayersChange={handleLayersChange}
          onNeedUrl={ensureAssetUrl}
        />

        {/* ── RIGHT: Layer panel ── */}
        <LayerPanel
          layers={layers}
          selectedId={selectedId}
          selectedIds={selectedIds}
          onSelect={selectLayer}
          onDelete={handleDelete}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          onLayerChange={handleLayerChange}
          onLayersChange={handleLayersChange}
          assets={folderAssets}
          onReplicate={handleReplicate}
        />
      </div>
    </div>
    </div>
  )
}

export default ReportCardTemplateDesigner
